// Messages de contact (formulaire public) : client axios écrit à la main (hors de src/API/
// régénéré), sur le modèle de ticketsClient. Tous les appels passent par l'instance axios partagée
// pour que l'intercepteur Bearer Auth0 s'applique en déployé (un fetch brut répond 401 là-bas).
// Le parsing est TOLÉRANT : l'API (construite en parallèle) peut répondre { items, countNew } ou un
// tableau nu, un compteur nombre nu ou { count } : chaque champ manquant retombe sur une valeur sûre.
import axios from "axios";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

/** Provoque le telechargement d'un blob sous le nom donne, puis libere l'URL objet. */
const triggerBlobDownload = (blob: Blob, fileName: string): void => {
	const url = window.URL.createObjectURL(blob);
	const anchorEl = document.createElement("a");
	anchorEl.href = url;
	anchorEl.download = fileName;
	document.body.appendChild(anchorEl);
	anchorEl.click();
	anchorEl.remove();
	window.URL.revokeObjectURL(url);
};

export type ContactMessageStatus = "new" | "read" | "archived";
export type ContactMessageEnv = "preview" | "production";

/** Un message reçu via le formulaire de contact d'un des deux sites publics. */
export interface ContactMessage {
	id: string;
	/** Site d'origine : « preview » ou « production ». */
	sourceEnv: ContactMessageEnv;
	name: string;
	email: string;
	subject?: string | null;
	/** Section d'origine du formulaire (libellé de page ou chemin) ; null pour l'historique. */
	section?: string | null;
	message: string;
	submittedAt: string;
	status: ContactMessageStatus;
	readBy?: string | null;
	readAt?: string | null;
}

/** Valeur sentinelle du filtre section : messages SANS section. Miroir du serveur. */
export const SECTION_NONE = "__none__";

export interface ContactMessageListResult {
	items: ContactMessage[];
	/** Nombre TOTAL de messages « new » (indépendant du filtre) : null si l'API ne l'a pas fourni. */
	countNew: number | null;
	/** Compteurs serveur des deux autres onglets ; null si l'API ne les a pas fournis. */
	countRead: number | null;
	countArchived: number | null;
}

export interface ContactMessageFilters {
	status?: ContactMessageStatus | "";
	skip?: number;
	take?: number;
	/** Section exacte, ou SECTION_NONE pour les messages sans section. */
	section?: string;
	/** Recherche serveur dans nom, e-mail, sujet et message. */
	search?: string;
}

export interface ContactSectionCount {
	section: string;
	count: number;
}

/** Coerce n'importe quelle réponse liste (tableau nu ou enveloppe { items }) en tableau. */
const asArray = (data: unknown): any[] => {
	if (Array.isArray(data)) return data;
	if (data && Array.isArray((data as any).items)) return (data as any).items;
	return [];
};

const toStatus = (raw: unknown): ContactMessageStatus =>
	raw === "read" || raw === "archived" ? raw : "new";

const toEnv = (raw: unknown): ContactMessageEnv => (raw === "production" ? "production" : "preview");

/** Une ligne de message tolérante : champs manquants → valeurs sûres. */
const toMessage = (raw: any): ContactMessage => ({
	id: String(raw?.id ?? ""),
	sourceEnv: toEnv(raw?.sourceEnv),
	name: String(raw?.name ?? ""),
	email: String(raw?.email ?? ""),
	subject: typeof raw?.subject === "string" && raw.subject.trim() ? raw.subject : null,
	section: typeof raw?.section === "string" && raw.section.trim() ? raw.section : null,
	message: String(raw?.message ?? ""),
	submittedAt: String(raw?.submittedAt ?? ""),
	status: toStatus(raw?.status),
	readBy: raw?.readBy ?? null,
	readAt: raw?.readAt ?? null
});

/** Compteur tolérant : nombre nu, { count } ou { countNew } → nombre fini, sinon 0. */
const toCount = (data: any): number => {
	const n = typeof data === "number" ? data : Number(data?.count ?? data?.countNew ?? 0);
	return Number.isFinite(n) ? n : 0;
};

export const contactMessagesClient = {
	/** Page de messages, filtrée par statut côté serveur (statut vide = tous). */
	list: async (filters: ContactMessageFilters = {}): Promise<ContactMessageListResult> => {
		const params: Record<string, unknown> = {
			skip: filters.skip ?? 0,
			take: filters.take ?? 25
		};
		if (filters.status) params.status = filters.status;
		if (filters.section) params.section = filters.section;
		if (filters.search && filters.search.trim()) params.q = filters.search.trim();
		const r = await axios.get(`${apiBase}/api/contact-messages`, { params, withCredentials: true });
		const data: any = r.data;
		const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
		const env = data && !Array.isArray(data) ? data : {};
		return {
			items: asArray(data).map(toMessage),
			countNew: num(env.countNew),
			countRead: num(env.countRead),
			countArchived: num(env.countArchived)
		};
	},

	/** Sections distinctes avec leur volume (SECTION_NONE = messages sans section). */
	sections: async (): Promise<ContactSectionCount[]> => {
		const r = await axios.get(`${apiBase}/api/contact-messages/sections`, { withCredentials: true });
		const rows = Array.isArray(r.data?.sections) ? r.data.sections : Array.isArray(r.data) ? r.data : [];
		return rows
			.map((s: any) => ({ section: String(s?.section ?? ""), count: Number(s?.count ?? 0) }))
			.filter((s: ContactSectionCount) => s.section.length > 0 && Number.isFinite(s.count));
	},

	/** Nombre de messages « new » : tolère un nombre nu OU une enveloppe { count }. */
	countNew: async (): Promise<number> => {
		const r = await axios.get(`${apiBase}/api/contact-messages/count-new`, { withCredentials: true });
		return toCount(r.data);
	},

	/**
	 * Telecharge le classeur Excel des messages, aux MEMES filtres que la boite affichee.
	 * L'export est une lecture : aucun statut n'est modifie. Le nom de fichier vient du serveur
	 * (en-tete Content-Disposition), avec un repli horodate si l'en-tete n'est pas expose.
	 */
	exportXlsx: async (filters: ContactMessageFilters = {}): Promise<void> => {
		const response = await axios.get(`${apiBase}/api/contact-messages/export`, {
			params: {
				status: filters.status || undefined,
				section: filters.section || undefined,
				q: filters.search || undefined
			},
			responseType: "blob",
			withCredentials: true
		});
		const disposition = (response.headers?.["content-disposition"] as string | undefined) ?? "";
		const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
		const fallback = `messages-contact-${new Date().toISOString().slice(0, 10)}.xlsx`;
		triggerBlobDownload(response.data as Blob, match ? decodeURIComponent(match[1]) : fallback);
	},

	/** Change le statut d'un message (« read », « archived », « new »). */
	setStatus: async (id: string, status: ContactMessageStatus): Promise<void> => {
		await axios.post(
			`${apiBase}/api/contact-messages/${encodeURIComponent(id)}/status`,
			{ status },
			{ withCredentials: true }
		);
	}
};
